import React, { useState, useEffect, useCallback } from 'react';
import {
  CCard, CCardBody, CCardHeader, CFormInput, CButton,
  CCol, CRow, CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody
} from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { cilSearch } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import debounce from 'lodash.debounce';
import InputMask from 'react-input-mask';
import PermissionsNoPage, { Permissions } from 'src/components/Permissions';

const normalizePhoneFilter = (value) => {
  if (!value) return '';
  const cleaned = value.replace(/\s+/g, '');
  if (!cleaned || cleaned === '+380') return '';
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
};

const page = 1;
const urlApi = '/app_personal_accounts';
const urlRouter = '/accounts';

export async function LoaderAccounts() {
  let res = await AxiosInstance.get(`${urlApi}?page=${page}`).then((response) => {
    return response;
  });
  return res;
}

const Accounts = ({ user_data }) => {
  // if (!Permissions(user_data, 'Accounts', 'VIEW_ACCOUNTS')) {
  //   return <PermissionsNoPage />;
  // }
  const { data } = useLoaderData();
  const navigate = useNavigate();

  const [rows, setRows] = useState(false);
  const [rowCountState, setRowCountState] = useState(0);

  // Фільтри
  const [phoneFilter, setPhoneFilter] = useState('');
  const [numberFilter, setNumberFilter] = useState('');

  // Початкові дані
  if (!rows) {
    setRowCountState(data['hydra:totalItems']);
    setRows(data['hydra:member']);
  }

  // --- Завантаження таблиці з фільтрами ---
  const getRowsTable = useCallback((p = 1) => {
    let request = '';
    const normalizedPhone = normalizePhoneFilter(phoneFilter);

    if (phoneFilter && !normalizedPhone) {
      return;
    }

    if (normalizedPhone) request += `&owner.phone=${encodeURIComponent(normalizedPhone)}`;
    if (numberFilter) request += `&number=${numberFilter}`;

    AxiosInstance.get(`${urlApi}?page=${p}${request}`).then((response) => {
      setRows(response.data['hydra:member']);
      setRowCountState(response.data['hydra:totalItems']);
    });
  }, [phoneFilter, numberFilter]);

  // --- debounce для пошуку ---
  const debouncedSearch = useCallback(
    debounce(() => {
      getRowsTable(1);
    }, 700),
    [getRowsTable]
  );

  // Запуск debounce при зміні фільтрів
  useEffect(() => {
    debouncedSearch();
    return debouncedSearch.cancel;
  }, [phoneFilter, numberFilter]);

  // --- Пагінація ---
  const handlePageChange = (page) => {
    getRowsTable(page + 1);
  };

  // --- Колонки ---
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'owner',
      headerName: 'Користувач (телефон)',
      width: 180,
      valueGetter: (params) => params.row.owner?.phone || '',
    },
    {
      field: 'pib',
      headerName: 'ПІБ',
      width: 250,
      valueGetter: (params) => params.row.owner?.firstName || '',
    },
    { field: 'number', headerName: 'Номер', headerAlign: 'center', align: 'center', width: 100 },
    {
      field: 'name',
      headerName: 'Назва',
      headerAlign: 'center',
      align: 'center',
      width: 150,
      renderCell: (params) => {
        return (
          <Link
            to={`${urlRouter}/view/${params.row.id}`}
            style={{ textDecoration: "none", color: "rgba(0, 0, 0, 0.87)" }}
            title="Редагувати"
          >
            {params.row.name}
          </Link>
        );
      }
    },
    { field: 'type', headerName: 'Тип', headerAlign: 'center', align: 'center', width: 100 },
    { field: 'fullAddress', headerName: 'Адреса', width: 500 },
    {
      field: "action",
      headerName: "Переглянути",
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      width: 110,
      renderCell: (params) => {
        return (
          <Link
            to={`${urlRouter}/view/${params.row.id}`}
            className='cutom-view-icon '
          >
            <CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
          </Link>
        );
      }
    },
  ];

  const handleClearFilters = useCallback(() => {
    setPhoneFilter('');
    setNumberFilter('');
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <CRow>
      <CCol xs={12}>
        <CCard className="mb-4">
          <CAccordion flush>
            <CAccordionItem>
              <CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
              <CAccordionBody>
                <CRow className="my-3 mx-2">
                  <CCol md={8}>
                    <CRow>
                      <CCol md={6} className='filter-field'>
                        <InputMask
                          mask="+380 99 999 99 99"
                          maskChar=""
                          value={phoneFilter}
                          onChange={(e) => setPhoneFilter(e.target.value)}
                        >
                          {(inputProps) => (
                            <CFormInput
                              {...inputProps}
                              placeholder="Користувач"
                              autoComplete="off"
                            />
                          )}
                        </InputMask>
                      </CCol>

                      <CCol md={6} className='filter-field'>
                        <CFormInput
                          placeholder="Номер рахунку"
                          value={numberFilter}
                          onChange={(e) => setNumberFilter(e.target.value)}
                          autoComplete="off"
                        />
                      </CCol>
                    </CRow>
                  </CCol>
                  <CCol md={4}>
                    <CButton color="secondary" variant="outline" onClick={handleClearFilters}>
                      Очистити фільтри
                    </CButton>
                  </CCol>
                </CRow>
              </CAccordionBody>
            </CAccordionItem>
          </CAccordion>

          <CCardHeader>
            <strong>Особові рахунки</strong>
          </CCardHeader>
          <CCardBody>
            <div style={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={30}
                rowsPerPageOptions={[30]}
                pagination
                rowCount={rowCountState}
                paginationMode="server"
                onPageChange={handlePageChange}
              />
            </div>
            <div className="d-flex justify-content-start mt-3">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Повернутись
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default Accounts;